import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, Loader2, UserRound, ShieldCheck, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/context/PermissionsContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { getTimezone } from '@/lib/clock';

// Diálogo global "Detalles del registro": quién registró y quién aprobó (el historial de cambios vive en Bitácora).
// Uso desde cualquier menú: detailsAction('contracts', id) → item de RowActionsMenu.
let openFn = null;
export const openRecordDetails = (entity, id, title) => openFn?.({ entity, id, title });
export const detailsAction = (entity, id, title) => ({ label: 'Detalles del registro', icon: Info, view: true, onClick: () => openRecordDetails(entity, id, title), testId: `details-${entity}-${id}` });

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { timeZone: getTimezone(), day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const REVIEW = { approved: 'Aprobado', pending: 'Pendiente de verificar', rejected: 'Rechazado' };

const Row = ({ icon: Icon, label, who, when, testId }) => (
  <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid={testId}>
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{who || '—'}</p>
      <p className="font-mono text-xs text-slate-500">{fmt(when)}</p>
    </div>
  </div>
);

export const RecordDetailsHost = () => {
  const [req, setReq] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { can } = usePermissions();
  useEffect(() => { openFn = setReq; return () => { openFn = null; }; }, []);
  useEffect(() => {
    if (!req) { setData(null); setError(null); return; }
    let alive = true;
    api.get(`/audit/records/${req.entity}/${req.id}/details`, { noCache: true })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'No se pudieron cargar los detalles'); });
    return () => { alive = false; };
  }, [req]);
  const close = () => setReq(null);
  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md" data-testid="record-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Info className="h-5 w-5 text-slate-600" />Detalles del registro</DialogTitle>
          <DialogDescription data-testid="record-details-label">{data ? data.entity_label : req?.title || ''}</DialogDescription>
        </DialogHeader>
        {error && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="record-details-error">{error}</p>}
        {!data && !error && <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {data && (
          <div className="space-y-3">
            <Row icon={UserRound} label="Registró" who={data.created?.by_name} when={data.created?.at} testId="record-details-created" />
            {data.review && (
              <Row icon={ShieldCheck} label={data.review.status === 'approved' ? 'Aprobó' : 'Verificación'}
                who={data.review.status === 'approved' ? data.review.by_name : REVIEW[data.review.status] || data.review.status}
                when={data.review.status === 'approved' ? data.review.at : null} testId="record-details-review" />
            )}
            {can('configuracion', 'bitacora') && (
              <Button variant="link" size="sm" className="h-7 px-0 text-indigo-600"
                onClick={() => { close(); navigate(data.last_event ? `/app/settings?seccion=usuarios&vista=bitacora&month=${data.last_event.month}&focus=${data.last_event.log_id}` : '/app/settings?seccion=usuarios&vista=bitacora&tab=changes'); }}
                data-testid="record-details-open-audit">
                <ExternalLink className="mr-1 h-3.5 w-3.5" />Ir a Bitácora
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
